// sentryQueryWrapper.ts
import {
  useQuery,
  useMutation,
  UseQueryOptions,
  UseMutationOptions,
  QueryKey,
  useInfiniteQuery,
} from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { handleErrorMessage } from './handleError';

export type SentryQueryOptions<TData, TError> = UseQueryOptions<TData, TError> & {
  defaultErrorMessage?: string;
};

type SentryMutationOptions<TData, TError, TVariables> = UseMutationOptions<
  TData,
  TError,
  TVariables
> & {
  defaultErrorMessage?: string;
};

// Wrapper for `useQuery` with Sentry tracing in v8
export function useSentryQuery<TData, TError>(
  queryKey: QueryKey,
  queryFn: () => Promise<TData>,
  options?: SentryQueryOptions<TData, TError>,
) {
  const { defaultErrorMessage, ...queryOption } = options ?? {};

  return useQuery<TData, TError>(
    queryKey,
    async () => {
      return await Sentry.startSpan(
        {
          name: `Query: ${queryKey[0]}`,
          op: 'query',
          attributes: { queryKey: JSON.stringify(queryKey) },
        },
        async (span) => {
          try {
            const result = await queryFn();
            return result;
          } catch (error: any) {
            // Add query-specific context to error
            Sentry.setContext('react_query', {
              type: 'query',
              queryKey: JSON.stringify(queryKey),
              defaultErrorMessage,
            });

            // Add tags for filtering
            Sentry.setTag('query_key', String(queryKey[0]));
            Sentry.setTag('operation_type', 'query');

            Sentry.captureException(error, {
              tags: {
                query_key: String(queryKey[0]),
                operation_type: 'query',
              },
            });

            if (defaultErrorMessage !== '')
              handleErrorMessage(
                { ...error?.response?.data, statusCode: error?.response?.status },
                defaultErrorMessage,
              );

            throw error;
          }
        },
      );
    },
    queryOption,
  );
}

// Wrapper for `useMutation` with Sentry tracing in v8
export function useSentryMutation<TData, TError, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: SentryMutationOptions<TData, TError, TVariables>,
) {
  const { defaultErrorMessage, ...queryOption } = options ?? {};

  return useMutation<TData, TError, TVariables>(async (variables: TVariables) => {
    return await Sentry.startSpan(
      {
        name: `Mutation: ${mutationFn.name || 'unnamed'}`,
        op: 'mutation',
      },
      async (span) => {
        try {
          const result = await mutationFn(variables);
          return result;
        } catch (error: any) {
          const httpStatus = error?.response?.status;

          // When defaultErrorMessage is '' the caller has opted into silent mode
          // (e.g. background price-preview calls). Skip both toasts and Sentry
          // noise for expected 422 validation failures in that case.
          const isSilentMode = defaultErrorMessage === '';
          const isValidationError = httpStatus === 422;

          if (!isSilentMode || !isValidationError) {
            // Add mutation-specific context to error
            Sentry.setContext('react_query', {
              type: 'mutation',
              mutationName: mutationFn.name || 'unnamed',
              defaultErrorMessage,
              variables:
                typeof variables === 'object' ? JSON.stringify(variables) : String(variables),
            });

            // Add tags for filtering
            Sentry.setTag('mutation_name', mutationFn.name || 'unnamed');
            Sentry.setTag('operation_type', 'mutation');

            Sentry.captureException(error, {
              tags: {
                mutation_name: mutationFn.name || 'unnamed',
                operation_type: 'mutation',
              },
            });
          }

          if (defaultErrorMessage !== '')
            handleErrorMessage(
              { ...error?.response?.data, statusCode: error?.response?.status },
              defaultErrorMessage,
              true,
            );

          throw error;
        }
      },
    );
  }, queryOption);
}

export function useSentryInfiniteQuery<TData, TError>(
  queryKey: QueryKey,
  queryFn: ({ pageParam }: { pageParam?: number }) => Promise<TData>,
  options?: {
    getNextPageParam: (lastPage: any, allPages: any[]) => number | undefined;
    defaultErrorMessage?: string;
    enabled?: boolean;
  },
) {
  const { defaultErrorMessage, ...queryOption } = options ?? {};

  return useInfiniteQuery<TData, TError>({
    queryKey,
    queryFn: async ({ pageParam = 1 }) => {
      return await Sentry.startSpan(
        {
          name: `InfiniteQuery: ${queryKey[0]}`,
          op: 'query',
          attributes: { queryKey: JSON.stringify(queryKey), pageParam },
        },
        async () => {
          try {
            return await queryFn({ pageParam });
          } catch (error: any) {
            // Add infinite query context
            Sentry.setContext('react_query', {
              type: 'infinite_query',
              queryKey: JSON.stringify(queryKey),
              pageParam,
              defaultErrorMessage,
            });

            Sentry.setTag('query_key', String(queryKey[0]));
            Sentry.setTag('operation_type', 'infinite_query');

            Sentry.captureException(error, {
              tags: {
                query_key: String(queryKey[0]),
                operation_type: 'infinite_query',
                page_param: String(pageParam),
              },
            });
            throw error;
          }
        },
      );
    },
    ...queryOption,
  });
}
